import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { scheduleAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Calendar, Filter, ChevronLeft, ChevronRight, MoreVertical, Edit, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { formatLocal } from '../lib/dateUtils';
import { toast } from 'sonner';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  missed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const formatStatus = (status) => {
  if (!status) return '';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

export default function TasksList() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 20;
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    loadTasks();
  }, [page, filters]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const params = {
        event_type: 'task',
        status: filters.status !== 'all' ? filters.status : undefined,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
      };

      const response = await scheduleAPI.getCalendarEvents(params);
      let allTasks = response.data.filter(event => event.event_type === 'task');

      // Client-side search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        allTasks = allTasks.filter(task =>
          task.title?.toLowerCase().includes(searchLower) ||
          task.description?.toLowerCase().includes(searchLower) ||
          task.lead_name?.toLowerCase().includes(searchLower)
        );
      }

      // Sort by updated_at or created_at (newest first)
      allTasks.sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at || 0);
        const dateB = new Date(b.updated_at || b.created_at || 0);
        return dateB - dateA;
      });

      // Pagination
      const total = Math.ceil(allTasks.length / itemsPerPage);
      setTotalPages(total);
      const startIdx = (page - 1) * itemsPerPage;
      const paginatedTasks = allTasks.slice(startIdx, startIdx + itemsPerPage);

      setTasks(paginatedTasks);
    } catch (error) {
      console.error('Failed to load tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      start_date: '',
      end_date: '',
    });
    setPage(1);
  };

  const handleStatusChange = async (task, newStatus) => {
    if (task.is_lead_activity) {
      toast.error('Lead tasks must be updated from the lead details page');
      return;
    }

    try {
      await scheduleAPI.updateEvent(task.id, { status: newStatus });
      toast.success('Status updated successfully');
      loadTasks();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleEditTask = (task) => {
    if (task.is_lead_activity) {
      toast.error('Lead tasks cannot be edited from here');
      return;
    }
    setEditingTask(task);
    setEditForm({
      title: task.title || '',
      description: task.description || '',
      start_time: task.start_time ? new Date(task.start_time).toISOString().slice(0, 16) : '',
      status: task.status || 'pending',
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.title || !editForm.start_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await scheduleAPI.updateEvent(editingTask.id, {
        ...editForm,
        start_time: new Date(editForm.start_time).toISOString(),
        end_time: new Date(editForm.start_time).toISOString(), // Tasks use same time
      });
      toast.success('Task updated successfully');
      setEditDialogOpen(false);
      loadTasks();
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error('Failed to update task');
    }
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleDeleteTask = async (task) => {
    if (task.is_lead_activity) {
      toast.error('Lead tasks cannot be deleted from here');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${task.title}"?`)) {
      return;
    }

    try {
      await scheduleAPI.deleteEvent(task.id);
      toast.success('Task deleted successfully');
      loadTasks();
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error('Failed to delete task');
    }
  };

  return (
    <div className="space-y-6" data-testid="tasks-list">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tasks</h1>
          <p className="text-muted-foreground mt-1">
            View and manage all tasks from schedule and leads
          </p>
        </div>
        <Button onClick={() => navigate('/schedule')}>
          <Calendar className="h-4 w-4 mr-2" />
          View Calendar
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Input
              placeholder="Search tasks..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
            
            <Select
              value={filters.status}
              onValueChange={(value) => handleFilterChange('status', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="missed">Missed</SelectItem>
              </SelectContent>
            </Select>

            <Input
              type="date"
              placeholder="Start Date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
            />

            <Input
              type="date"
              placeholder="End Date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
            />
          </div>
          <div className="mt-4">
            <Button variant="outline" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tasks Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8">Loading tasks...</div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No tasks found
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Lead</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tasks.map((task) => (
                    <TableRow key={task.id}>
                      <TableCell className="font-medium">
                        {task.title}
                      </TableCell>
                      <TableCell>
                        {formatLocal(task.start_time, 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={`${statusColors[task.status]} inline-flex items-center justify-center min-w-[90px]`}>
                          {formatStatus(task.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {task.is_lead_activity ? (
                          <div>
                            {task.related_to ? (
                              <button
                                onClick={() => navigate(`/leads/${task.related_to}`)}
                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
                              >
                                {task.lead_name}
                              </button>
                            ) : (
                              <div>{task.lead_name}</div>
                            )}
                            {task.lead_phone && (
                              <div className="text-xs text-muted-foreground">{task.lead_phone}</div>
                            )}
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {task.description || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {!task.is_lead_activity ? (
                          <div className="flex items-center justify-end gap-1">
                            {/* Edit Icon */}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleEditTask(task)}
                              title="Edit Task"
                            >
                              <Edit className="h-4 w-4 text-blue-600" />
                            </Button>
                            
                            {/* Delete Icon */}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleDeleteTask(task)}
                              title="Delete Task"
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                            
                            {/* Kebab Menu for Status Changes */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Change Status">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleStatusChange(task, 'pending')}>
                                  Mark as Pending
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusChange(task, 'completed')}>
                                  Mark as Completed
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusChange(task, 'missed')}>
                                  Mark as Missed
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">Lead Task</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Task Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
            <DialogDescription>
              Update the task details below
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-title">Task Title *</Label>
              <Input
                id="edit-title"
                value={editForm.title || ''}
                onChange={(e) => handleEditFormChange('title', e.target.value)}
                placeholder="Task title"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={editForm.description || ''}
                onChange={(e) => handleEditFormChange('description', e.target.value)}
                placeholder="Task description"
                rows={3}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-start-time">Due Date *</Label>
              <Input
                id="edit-start-time"
                type="datetime-local"
                value={editForm.start_time || ''}
                onChange={(e) => handleEditFormChange('start_time', e.target.value)}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-status">Status</Label>
              <Select
                value={editForm.status || 'pending'}
                onValueChange={(value) => handleEditFormChange('status', value)}
              >
                <SelectTrigger id="edit-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="missed">Missed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
